import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { comics } from "@/mocks/comics";

const heroComics = comics.slice(0, 5);

export default function HeroCarousel() {
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goTo = useCallback((index: number) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrent(index);
    setTimeout(() => setIsTransitioning(false), 500);
  }, [isTransitioning]);

  const next = useCallback(() => {
    goTo((current + 1) % heroComics.length);
  }, [current, goTo]);

  useEffect(() => {
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next]);

  return (
    <section className="relative w-full h-[420px] md:h-[520px] overflow-hidden">
      {heroComics.map((comic, index) => (
        <div
          key={comic.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === current ? "opacity-100 z-10" : "opacity-0 z-0"
          }`}
        >
          <img
            src={comic.cover}
            alt={comic.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-black/20"></div>
        </div>
      ))}

      <div className="absolute inset-0 z-20 flex items-end">
        <div className="max-w-[1280px] mx-auto w-full px-4 md:px-6 pb-10 md:pb-14">
          <div className="max-w-xl">
            <div className="flex items-center gap-2 mb-3">
              {heroComics[current].tags.slice(0, 2).map((tag) => (
                <span key={tag} className="px-2.5 py-1 rounded-full bg-white/20 backdrop-blur-sm text-white text-xs font-medium">
                  {tag}
                </span>
              ))}
              <span className={`px-2.5 py-1 rounded-full text-white text-xs font-medium ${
                heroComics[current].status === "连载中" ? "bg-primary-500/80" : "bg-accent-500/80"
              }`}>
                {heroComics[current].status}
              </span>
            </div>
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-white mb-3">
              {heroComics[current].title}
            </h2>
            <p className="text-sm md:text-base text-white/80 line-clamp-2 mb-5 leading-relaxed">
              {heroComics[current].description}
            </p>
            <div className="flex items-center gap-3">
              <Link
                to={`/article/${heroComics[current].id}`}
                className="px-6 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
              >
                立即阅读
              </Link>
              <Link
                to={`/category/${heroComics[current].category}`}
                className="px-6 py-2.5 rounded-full bg-white/15 backdrop-blur-sm text-white text-sm font-medium hover:bg-white/25 transition-colors duration-200 whitespace-nowrap"
              >
                更多同类
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 right-6 z-30 flex items-center gap-1.5">
        {heroComics.map((_, index) => (
          <button
            key={index}
            onClick={() => goTo(index)}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              index === current
                ? "w-6 bg-primary-500"
                : "bg-white/40 hover:bg-white/60"
            }`}
            aria-label={`切换到第 ${index + 1} 张`}
          />
        ))}
      </div>

      <button
        onClick={() => goTo((current - 1 + heroComics.length) % heroComics.length)}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-30 w-10 h-10 rounded-full bg-white/15 backdrop-blur-sm text-white hover:bg-white/30 transition-all duration-200 flex items-center justify-center"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-xl"></i>
      </button>
      <button
        onClick={next}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-30 w-10 h-10 rounded-full bg-white/15 backdrop-blur-sm text-white hover:bg-white/30 transition-all duration-200 flex items-center justify-center"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-xl"></i>
      </button>
    </section>
  );
}